import { CITY_KEYS, CITY_POINTS, OBLAST_CENTERS } from 'constants/ukraineCities';

/**
 * Клуб на карті України — ADR-0014.
 *
 * Публічне API e-phan віддає адресу клубу одним рядком і без координат:
 *
 *   "Бердичів, Житомирська область, Україна, 13300"
 *   "вулиця Казимира Малевича, 86Л, Київ, Украина, 03150"
 *   "Київ, Україна, 02000"
 *
 * Тобто точний адрес є в одиниць, у решти — місто, область, індекс, причому
 * країна трапляється і українською, і російською. Звідси спосіб: у рядку
 * шукається населений пункт, а координати беруться з довідника
 * (constants/ukraineCities). Геокодер стороннього сервісу не залучається —
 * сторінка не має ходити по мережі за кожним клубом, а точність до міста для
 * оглядової карти достатня.
 */

/**
 * Згортання назви для порівняння.
 *
 * Різні джерела пишуть ту саму назву по-різному: «Київ» і «Киев», «Мар'їнка»
 * і «Марьинка», «Івано-Франківськ» і «Івано Франківськ». Тому літери, що
 * розрізняють українське й російське написання, зводяться до однієї, апострофи
 * прибираються, дефіс прирівнюється до пробілу.
 */
export function foldName(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[’'`ʼ‘]/g, '')
    .replace(/[іїйы]/g, 'и')
    .replace(/[єэё]/g, 'е')
    .replace(/ґ/g, 'г')
    .replace(/[-–—]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Обласні центри за згорнутою назвою області. */
const OBLAST_KEYS = Object.entries(OBLAST_CENTERS).reduce((acc, [oblast, center]) => {
  acc[foldName(oblast)] = center;
  return acc;
}, {});

const COUNTRY = /^(украина|ukraine|ua)$/;
/* Межа слова \b тут не годиться: вона рахує словом лише латиницю й цифри,
   тому між пробілом і кирилицею її просто немає. */
const OBLAST = /(^|\s)обл(асть|\.)?$/;
const POSTCODE = /^\d[\d\s-]*$/;

/** Назва області без слова «область»: «Житомирська область» -> «житомирська». */
const oblastKey = (part) => foldName(part.replace(OBLAST, ''));

function lookupCity(part) {
  const index = CITY_KEYS[foldName(part)];
  return index === undefined ? null : index;
}

/**
 * Адреса -> точка на карті.
 *
 * Місто шукається з кінця адреси, а не з початку: у повній адресі спершу йде
 * вулиця з будинком, і саме передостанній перед країною шматок — населений
 * пункт. Якщо міста в довіднику немає (село, селище), береться центр області,
 * і точка позначається наближеною.
 *
 * @param {string} address рядок адреси з e-phan
 * @returns {{ index: number, lat: number, lon: number, city: string, approximate: boolean }|null}
 */
/** Частини адреси до країни: вулиця, будинок, місто, область. Індекс відкинуто. */
function localParts(address) {
  const parts = String(address || '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && !POSTCODE.test(part));

  const countryAt = parts.findIndex((part) => COUNTRY.test(foldName(part)));

  return countryAt === -1 ? parts : parts.slice(0, countryAt);
}

/**
 * Населений пункт так, як його написали в адресі, — для підпису.
 *
 * На відміну від locateAddress тут немає довідника: назва не звіряється ні з
 * чим, тож підпис не підміняє село обласним центром. Місто — останній шматок
 * перед країною, який не є областю.
 */
export function addressCity(address) {
  const local = localParts(address);

  for (let i = local.length - 1; i >= 0; i--) {
    if (!OBLAST.test(foldName(local[i]))) return local[i];
  }

  return null;
}

export function locateAddress(address) {
  const local = localParts(address);

  if (local.length === 0) return null;

  let oblast = null;

  for (let i = local.length - 1; i >= 0; i--) {
    const part = local[i];

    if (OBLAST.test(foldName(part))) {
      if (!oblast) oblast = part;
      continue;
    }

    const index = lookupCity(part);
    if (index !== null) {
      const [lat, lon] = CITY_POINTS[index];
      return { index, lat, lon, city: part, approximate: false };
    }
  }

  // Міста в довіднику немає: лишається область.
  if (oblast) {
    const center = OBLAST_KEYS[oblastKey(oblast)];
    const index = center === undefined ? null : lookupCity(center);

    if (index !== null) {
      const [lat, lon] = CITY_POINTS[index];
      return { index, lat, lon, city: center, approximate: true };
    }
  }

  return null;
}

/**
 * Одна адреса клубу -> точка.
 *
 * У локації є власне поле `city` — так його побачив геокодер, тією мовою, якою
 * адресу вводили. Воно надійніше за розбір рядка, тому пробується першим, а
 * повний рядок лишається запасним: у записів, заведених до появи карти в
 * e-phan, міста в окремому полі немає.
 */
export function locateLocation(location) {
  if (!location) return null;

  const byCity = location.city ? lookupCity(location.city) : null;

  if (byCity !== null) {
    const [lat, lon] = CITY_POINTS[byCity];
    return { index: byCity, lat, lon, city: String(location.city).trim(), approximate: false };
  }

  return locateAddress(location.address);
}

/**
 * Усі точки клубу — ADR-0016.
 *
 * У клубу може бути кілька адрес: головна й філії. На карті клуб має стояти в
 * кожному місті, де він працює, — але рівно один раз у кожному: дві філії в
 * одному місті це одна точка, а не дві.
 *
 * Запасний варіант для записів без адрес — місто в дужках назви («Ритм (Київ)»):
 * так підписували клуби до появи адрес, і такі назви в реєстрі ще лишилися.
 */
export function locateClub(club) {
  if (!club) return [];

  const locations = Array.isArray(club.locations) && club.locations.length > 0
    ? club.locations
    : [{ city: club.city, address: club.address }];

  const found = [];
  const seen = new Set();

  locations.forEach((location) => {
    const point = locateLocation(location);
    if (!point || seen.has(point.index)) return;
    seen.add(point.index);
    found.push(point);
  });

  if (found.length > 0) return found;

  const name = String(club.name || '');
  const inBrackets = name.includes('(') ? name.split('(')[1].split(')')[0] : null;
  const byName = locateAddress(inBrackets);

  return byName ? [byName] : [];
}

/**
 * Клуби -> точки карти.
 *
 * Клуби одного міста стають однією точкою: інакше в Києві маркери лягли б
 * один на один. Клуб із філіями в різних містах стоїть у кожному з них —
 * карта відповідає на питання «де можна танцювати», а не «де зареєстровано».
 * Підпис точки — те написання міста, яке трапляється найчастіше серед знайдених
 * точно; коли точних немає, лишається центр області.
 *
 * `placed` рахує самі клуби, а не маркери: клуб із двох міст лишається одним
 * клубом. Ті, чийого міста визначити не вдалося, повертаються окремим списком —
 * сторінка згадує їх числом, а не мовчки викидає.
 *
 * @returns {{ places: Array, placed: number, unplaced: Array }}
 */
export function groupClubsByPlace(clubs) {
  const places = new Map();
  const unplaced = [];
  let placed = 0;

  (clubs || []).forEach((club) => {
    const found = locateClub(club);

    if (found.length === 0) {
      unplaced.push(club);
      return;
    }

    placed += 1;

    found.forEach((point) => {
      if (!places.has(point.index)) {
        places.set(point.index, {
          id: String(point.index),
          lat: point.lat,
          lon: point.lon,
          clubs: [],
          names: new Map(),
          approximate: true,
        });
      }

      const place = places.get(point.index);
      place.clubs.push({ ...club, place: point.city, approximate: point.approximate });

      if (!point.approximate) {
        place.approximate = false;
        place.names.set(point.city, (place.names.get(point.city) || 0) + 1);
      } else if (place.names.size === 0) {
        place.names.set(point.city, 0);
      }
    });
  });

  const list = [...places.values()].map((place) => {
    const city = [...place.names.entries()].sort((a, b) => b[1] - a[1])[0][0];
    return { id: place.id, lat: place.lat, lon: place.lon, city, approximate: place.approximate, clubs: place.clubs };
  });

  // Більші точки малюються першими, щоб дрібні не ховалися під ними.
  list.sort((a, b) => b.clubs.length - a.clubs.length || (a.city < b.city ? -1 : 1));

  return { places: list, placed, unplaced };
}
